import Dexie, { type Table } from 'dexie'

import type { Task } from '../types/task'
import type { WizardFarm } from '../types/farmWizard'
import type { Lot, LotEvent } from '../types/lot'
import type { ActivityEvent } from '../types/activity'
import type { FeedLog, FishStockLog, HarvestLog, LotCostEvent, MortalityLog, WeightSampleLog } from '../types/production'
import type { BiomassDataRow } from '../utils/productionUnitTable'
import type { InventoryItem } from '../types/inventory'

export type StoredLotEvent = LotEvent & {
  lotIds: string[]
  cacheState?: 'lineage-empty'
}

export type TaskOutboxType = 'upsert' | 'delete'

export type TaskOutboxEntry = {
  seq?: number
  type: TaskOutboxType
  taskId: string
  createdAt: string
}

export type EntityOutboxEntityType =
  | 'farm'
  | 'productionUnit'
  | 'lot'
  | 'lotMoveCommand'
  | 'lotTransferCommand'
  | 'lotSplitCommand'
  | 'lotMergeCommand'
  | 'waterLog'
  | 'productionRecord'
  | 'feedLog'
  | 'mortalityLog'
  | 'weightSampleLog'
  | 'fishStockLog'
  | 'harvestLog'
  | 'lotCostEvent'
  | 'inventoryItem'
  | 'task'
  | 'mortalityMatrix'
  | 'sfrMatrix'

// Crew Mode Charter D-4 shared identity mechanism: offline-cacheable roster
// of tenant members who have an operator PIN set, refreshed opportunistically
// whenever online. `pinHash` is a PBKDF2 string (see
// src/utils/operatorPinCrypto.ts), never the raw PIN — PIN verification
// happens entirely client-side against this cache, with zero connectivity
// required, per Charter O-5.
export type StoredOperatorRosterEntry = {
  tenantMembershipId: string
  actorId: string
  displayName: string
  pinHash: string
  pinUpdatedAt: string
}
export type EntityOutboxOp = 'upsert' | 'delete'

export type EntityOutboxEntry = {
  seq?: number
  entityType: EntityOutboxEntityType
  op: EntityOutboxOp
  entityId: string
  payload?: unknown
  /** Findings #66: why a feed / mortality / weight-sample / water log was
   *  deleted. Sent with the DELETE, which the server audits. */
  reason?: string
  /** Crew Mode undo: the operator (PIN session) who removed their own entry. */
  operatorActorId?: string
  createdAt: string
  attemptCount?: number
  lastError?: string
  /** Findings #123: HTTP status of the last failed attempt (403 = no permission). */
  lastErrorStatus?: number
}

export class SundayOfflineDb extends Dexie {
  tasks!: Table<Task, string>
  taskOutbox!: Table<TaskOutboxEntry, number>
  farms!: Table<WizardFarm, string>
  lots!: Table<Lot, string>
  lotEvents!: Table<StoredLotEvent, string>
  feedLogs!: Table<FeedLog, string>
  mortalityLogs!: Table<MortalityLog, string>
  weightSampleLogs!: Table<WeightSampleLog, string>
  fishStockLogs!: Table<FishStockLog, string>
  harvestLogs!: Table<HarvestLog, string>
  lotCostEvents!: Table<LotCostEvent, string>
  inventoryItems!: Table<InventoryItem, string>
  productionTableRows!: Table<BiomassDataRow & { pk: string; farmId: string; productionUnitId: string; updatedAt: string }, string>
  entityOutbox!: Table<EntityOutboxEntry, number>
  activityEvents!: Table<ActivityEvent, string>
  operatorRoster!: Table<StoredOperatorRosterEntry, string>

  constructor(dbName: string) {
    super(dbName)

    // Version 1: Tasks + a minimal outbox for future sync.
    this.version(1).stores({
      tasks: '&id, updatedAt, completed, assigneeId, dueAt, parentId',
      taskOutbox: '++seq, taskId, type, createdAt'
    })

    // Version 2: Context data (farms + lots).
    this.version(2).stores({
      tasks: '&id, updatedAt, completed, assigneeId, dueAt, parentId',
      taskOutbox: '++seq, taskId, type, createdAt',
      farms: '&id, name, createdAt',
      lots: '&id, farmId, productionUnitId, lotKey, stage, updatedAt'
    })

    // Version 3: Generic entity outbox for farms/lots/production units sync.
    this.version(3).stores({
      tasks: '&id, updatedAt, completed, assigneeId, dueAt, parentId',
      taskOutbox: '++seq, taskId, type, createdAt',
      farms: '&id, name, createdAt',
      lots: '&id, farmId, productionUnitId, lotKey, stage, updatedAt',
      entityOutbox: '++seq, entityType, op, entityId, createdAt'
    })

    // Version 4: Add compound indexes for common queries.
    this.version(4).stores({
      tasks: '&id, updatedAt, completed, assigneeId, dueAt, parentId',
      taskOutbox: '++seq, taskId, type, createdAt',
      farms: '&id, name, createdAt',
      lots: '&id, farmId, productionUnitId, [farmId+productionUnitId], stage, [farmId+stage], [farmId+productionUnitId+stage], lotKey, updatedAt',
      entityOutbox: '++seq, entityType, op, entityId, [entityType+entityId], createdAt'
    })

    // Version 5: Activity events (Recent Activity feed).
    this.version(5).stores({
      tasks: '&id, updatedAt, completed, assigneeId, dueAt, parentId',
      taskOutbox: '++seq, taskId, type, createdAt',
      farms: '&id, name, createdAt',
      lots: '&id, farmId, productionUnitId, [farmId+productionUnitId], stage, [farmId+stage], [farmId+productionUnitId+stage], lotKey, updatedAt',
      entityOutbox: '++seq, entityType, op, entityId, [entityType+entityId], createdAt',
      activityEvents: '&id, createdAt, source, level, dedupeKey, [dedupeKey+createdAt], farmId, productionUnitId, lotId'
    })

    // Version 6: Feed logs (Actual fed events).
    this.version(6).stores({
      tasks: '&id, updatedAt, completed, assigneeId, dueAt, parentId',
      taskOutbox: '++seq, taskId, type, createdAt',
      farms: '&id, name, createdAt',
      lots: '&id, farmId, productionUnitId, [farmId+productionUnitId], stage, [farmId+stage], [farmId+productionUnitId+stage], lotKey, updatedAt',
      feedLogs: '&id, farmId, productionUnitId, recordedAt, lot, [farmId+productionUnitId], [farmId+productionUnitId+recordedAt]',
      entityOutbox: '++seq, entityType, op, entityId, [entityType+entityId], createdAt',
      activityEvents: '&id, createdAt, source, level, dedupeKey, [dedupeKey+createdAt], farmId, productionUnitId, lotId'
    })

    // Version 7: Production table rows (large offline storage; replaces localStorage blobs).
    this.version(7).stores({
      tasks: '&id, updatedAt, completed, assigneeId, dueAt, parentId',
      taskOutbox: '++seq, taskId, type, createdAt',
      farms: '&id, name, createdAt',
      lots: '&id, farmId, productionUnitId, [farmId+productionUnitId], stage, [farmId+stage], [farmId+productionUnitId+stage], lotKey, updatedAt',
      feedLogs: '&id, farmId, productionUnitId, recordedAt, lot, [farmId+productionUnitId], [farmId+productionUnitId+recordedAt]',
      productionTableRows: '&pk, id, date, farmId, productionUnitId, [farmId+productionUnitId], [farmId+productionUnitId+date], lot, lotId, stage, archived, [farmId+productionUnitId+archived], updatedAt',
      entityOutbox: '++seq, entityType, op, entityId, [entityType+entityId], createdAt',
      activityEvents: '&id, createdAt, source, level, dedupeKey, [dedupeKey+createdAt], farmId, productionUnitId, lotId'
    })

    // Version 8: Add archived partition indexes for production rows.
    this.version(8).stores({
      tasks: '&id, updatedAt, completed, assigneeId, dueAt, parentId',
      taskOutbox: '++seq, taskId, type, createdAt',
      farms: '&id, name, createdAt',
      lots: '&id, farmId, productionUnitId, [farmId+productionUnitId], stage, [farmId+stage], [farmId+productionUnitId+stage], lotKey, updatedAt',
      feedLogs: '&id, farmId, productionUnitId, recordedAt, lot, [farmId+productionUnitId], [farmId+productionUnitId+recordedAt]',
      productionTableRows: '&pk, id, date, farmId, productionUnitId, [farmId+productionUnitId], [farmId+productionUnitId+date], lot, lotId, stage, archived, [farmId+productionUnitId+archived], updatedAt',
      entityOutbox: '++seq, entityType, op, entityId, [entityType+entityId], createdAt',
      activityEvents: '&id, createdAt, source, level, dedupeKey, [dedupeKey+createdAt], farmId, productionUnitId, lotId'
    })

    // Version 9: Inventory items (Warehouse) stored in IndexedDB for cross-device sync.
    this.version(9).stores({
      tasks: '&id, updatedAt, completed, assigneeId, dueAt, parentId',
      taskOutbox: '++seq, taskId, type, createdAt',
      farms: '&id, name, createdAt',
      lots: '&id, farmId, productionUnitId, [farmId+productionUnitId], stage, [farmId+stage], [farmId+productionUnitId+stage], lotKey, updatedAt',
      feedLogs: '&id, farmId, productionUnitId, recordedAt, lot, [farmId+productionUnitId], [farmId+productionUnitId+recordedAt]',
      inventoryItems: '&id, farmId, category, [farmId+category], updatedAt, name',
      productionTableRows: '&pk, id, date, farmId, productionUnitId, [farmId+productionUnitId], [farmId+productionUnitId+date], lot, lotId, stage, archived, [farmId+productionUnitId+archived], updatedAt',
      entityOutbox: '++seq, entityType, op, entityId, [entityType+entityId], createdAt',
      activityEvents: '&id, createdAt, source, level, dedupeKey, [dedupeKey+createdAt], farmId, productionUnitId, lotId'
    })

    // Version 10: Cached lot-event ledger history for offline read-side support.
    this.version(10).stores({
      tasks: '&id, updatedAt, completed, assigneeId, dueAt, parentId',
      taskOutbox: '++seq, taskId, type, createdAt',
      farms: '&id, name, createdAt',
      lots: '&id, farmId, productionUnitId, [farmId+productionUnitId], stage, [farmId+stage], [farmId+productionUnitId+stage], lotKey, updatedAt',
      lotEvents: '&id, recordedAt, lineageRootId, *lotIds, eventType, updatedAt',
      feedLogs: '&id, farmId, productionUnitId, recordedAt, lot, [farmId+productionUnitId], [farmId+productionUnitId+recordedAt]',
      inventoryItems: '&id, farmId, category, [farmId+category], updatedAt, name',
      productionTableRows: '&pk, id, date, farmId, productionUnitId, [farmId+productionUnitId], [farmId+productionUnitId+date], lot, lotId, stage, archived, [farmId+productionUnitId+archived], updatedAt',
      entityOutbox: '++seq, entityType, op, entityId, [entityType+entityId], createdAt',
      activityEvents: '&id, createdAt, source, level, dedupeKey, [dedupeKey+createdAt], farmId, productionUnitId, lotId'
    })

    // Version 11: Mortality logs (dedicated mortality event boundary).
    this.version(11).stores({
      tasks: '&id, updatedAt, completed, assigneeId, dueAt, parentId',
      taskOutbox: '++seq, taskId, type, createdAt',
      farms: '&id, name, createdAt',
      lots: '&id, farmId, productionUnitId, [farmId+productionUnitId], stage, [farmId+stage], [farmId+productionUnitId+stage], lotKey, updatedAt',
      lotEvents: '&id, recordedAt, lineageRootId, *lotIds, eventType, updatedAt',
      feedLogs: '&id, farmId, productionUnitId, recordedAt, lot, [farmId+productionUnitId], [farmId+productionUnitId+recordedAt]',
      mortalityLogs: '&id, farmId, productionUnitId, recordedAt, lot, [farmId+productionUnitId], [farmId+productionUnitId+recordedAt]',
      inventoryItems: '&id, farmId, category, [farmId+category], updatedAt, name',
      productionTableRows: '&pk, id, date, farmId, productionUnitId, [farmId+productionUnitId], [farmId+productionUnitId+date], lot, lotId, stage, archived, [farmId+productionUnitId+archived], updatedAt',
      entityOutbox: '++seq, entityType, op, entityId, [entityType+entityId], createdAt',
      activityEvents: '&id, createdAt, source, level, dedupeKey, [dedupeKey+createdAt], farmId, productionUnitId, lotId'
    })

    // Version 12: Weight sample logs (per-event Sampling weight evidence, mirrors the mortalityLogs shape).
    this.version(12).stores({
      tasks: '&id, updatedAt, completed, assigneeId, dueAt, parentId',
      taskOutbox: '++seq, taskId, type, createdAt',
      farms: '&id, name, createdAt',
      lots: '&id, farmId, productionUnitId, [farmId+productionUnitId], stage, [farmId+stage], [farmId+productionUnitId+stage], lotKey, updatedAt',
      lotEvents: '&id, recordedAt, lineageRootId, *lotIds, eventType, updatedAt',
      feedLogs: '&id, farmId, productionUnitId, recordedAt, lot, [farmId+productionUnitId], [farmId+productionUnitId+recordedAt]',
      mortalityLogs: '&id, farmId, productionUnitId, recordedAt, lot, [farmId+productionUnitId], [farmId+productionUnitId+recordedAt]',
      weightSampleLogs: '&id, farmId, productionUnitId, recordedAt, lot, productionRowId, [farmId+productionUnitId], [farmId+productionUnitId+recordedAt]',
      inventoryItems: '&id, farmId, category, [farmId+category], updatedAt, name',
      productionTableRows: '&pk, id, date, farmId, productionUnitId, [farmId+productionUnitId], [farmId+productionUnitId+date], lot, lotId, stage, archived, [farmId+productionUnitId+archived], updatedAt',
      entityOutbox: '++seq, entityType, op, entityId, [entityType+entityId], createdAt',
      activityEvents: '&id, createdAt, source, level, dedupeKey, [dedupeKey+createdAt], farmId, productionUnitId, lotId'
    })

    // Version 13: Fish stock logs (T3-A-02, first Tier A event stream of the
    // T3-A stock-count cluster, mirrors the weightSampleLogs shape).
    this.version(13).stores({
      tasks: '&id, updatedAt, completed, assigneeId, dueAt, parentId',
      taskOutbox: '++seq, taskId, type, createdAt',
      farms: '&id, name, createdAt',
      lots: '&id, farmId, productionUnitId, [farmId+productionUnitId], stage, [farmId+stage], [farmId+productionUnitId+stage], lotKey, updatedAt',
      lotEvents: '&id, recordedAt, lineageRootId, *lotIds, eventType, updatedAt',
      feedLogs: '&id, farmId, productionUnitId, recordedAt, lot, [farmId+productionUnitId], [farmId+productionUnitId+recordedAt]',
      mortalityLogs: '&id, farmId, productionUnitId, recordedAt, lot, [farmId+productionUnitId], [farmId+productionUnitId+recordedAt]',
      weightSampleLogs: '&id, farmId, productionUnitId, recordedAt, lot, productionRowId, [farmId+productionUnitId], [farmId+productionUnitId+recordedAt]',
      fishStockLogs: '&id, farmId, productionUnitId, recordedAt, lot, productionRowId, [farmId+productionUnitId], [farmId+productionUnitId+recordedAt]',
      inventoryItems: '&id, farmId, category, [farmId+category], updatedAt, name',
      productionTableRows: '&pk, id, date, farmId, productionUnitId, [farmId+productionUnitId], [farmId+productionUnitId+date], lot, lotId, stage, archived, [farmId+productionUnitId+archived], updatedAt',
      entityOutbox: '++seq, entityType, op, entityId, [entityType+entityId], createdAt',
      activityEvents: '&id, createdAt, source, level, dedupeKey, [dedupeKey+createdAt], farmId, productionUnitId, lotId'
    })

    // Version 14: Lot cost events (T3-B-02, Tier A event stream of the
    // T3-B cost cluster; auto-derived from feed/labor/medication sources
    // via the unique [tenantKey, sourceEventType, sourceEventId] server index).
    this.version(14).stores({
      tasks: '&id, updatedAt, completed, assigneeId, dueAt, parentId',
      taskOutbox: '++seq, taskId, type, createdAt',
      farms: '&id, name, createdAt',
      lots: '&id, farmId, productionUnitId, [farmId+productionUnitId], stage, [farmId+stage], [farmId+productionUnitId+stage], lotKey, updatedAt',
      lotEvents: '&id, recordedAt, lineageRootId, *lotIds, eventType, updatedAt',
      feedLogs: '&id, farmId, productionUnitId, recordedAt, lot, [farmId+productionUnitId], [farmId+productionUnitId+recordedAt]',
      mortalityLogs: '&id, farmId, productionUnitId, recordedAt, lot, [farmId+productionUnitId], [farmId+productionUnitId+recordedAt]',
      weightSampleLogs: '&id, farmId, productionUnitId, recordedAt, lot, productionRowId, [farmId+productionUnitId], [farmId+productionUnitId+recordedAt]',
      fishStockLogs: '&id, farmId, productionUnitId, recordedAt, lot, productionRowId, [farmId+productionUnitId], [farmId+productionUnitId+recordedAt]',
      lotCostEvents: '&id, farmId, productionUnitId, lotId, recordedAt, costType, sourceEventType, sourceEventId, [farmId+productionUnitId], [farmId+productionUnitId+recordedAt], [lotId+recordedAt]',
      inventoryItems: '&id, farmId, category, [farmId+category], updatedAt, name',
      productionTableRows: '&pk, id, date, farmId, productionUnitId, [farmId+productionUnitId], [farmId+productionUnitId+date], lot, lotId, stage, archived, [farmId+productionUnitId+archived], updatedAt',
      entityOutbox: '++seq, entityType, op, entityId, [entityType+entityId], createdAt',
      activityEvents: '&id, createdAt, source, level, dedupeKey, [dedupeKey+createdAt], farmId, productionUnitId, lotId'
    })

    // Version 15: T3-B-03a lot cost event accounting rework. The old
    // `costType` enum was dropped and replaced by the full accounting model
    // (`categoryPrefix`, `categorySequence`, `categoryCode`, `expenseNature`,
    // `costClassification`). Indexed the accounting facets that the client and
    // reports filter by. Pre-v15 rows carried a `costType` field and no
    // accounting fields; T3-B-01/02 landed data-plumbing only, so no
    // production data existed — we clear the local mirror on upgrade and
    // let the next remote list() re-hydrate live rows.
    this.version(15).stores({
      tasks: '&id, updatedAt, completed, assigneeId, dueAt, parentId',
      taskOutbox: '++seq, taskId, type, createdAt',
      farms: '&id, name, createdAt',
      lots: '&id, farmId, productionUnitId, [farmId+productionUnitId], stage, [farmId+stage], [farmId+productionUnitId+stage], lotKey, updatedAt',
      lotEvents: '&id, recordedAt, lineageRootId, *lotIds, eventType, updatedAt',
      feedLogs: '&id, farmId, productionUnitId, recordedAt, lot, [farmId+productionUnitId], [farmId+productionUnitId+recordedAt]',
      mortalityLogs: '&id, farmId, productionUnitId, recordedAt, lot, [farmId+productionUnitId], [farmId+productionUnitId+recordedAt]',
      weightSampleLogs: '&id, farmId, productionUnitId, recordedAt, lot, productionRowId, [farmId+productionUnitId], [farmId+productionUnitId+recordedAt]',
      fishStockLogs: '&id, farmId, productionUnitId, recordedAt, lot, productionRowId, [farmId+productionUnitId], [farmId+productionUnitId+recordedAt]',
      lotCostEvents: '&id, farmId, productionUnitId, lotId, recordedAt, categoryPrefix, categoryCode, costClassification, sourceEventType, sourceEventId, [farmId+productionUnitId], [farmId+productionUnitId+recordedAt], [lotId+recordedAt]',
      inventoryItems: '&id, farmId, category, [farmId+category], updatedAt, name',
      productionTableRows: '&pk, id, date, farmId, productionUnitId, [farmId+productionUnitId], [farmId+productionUnitId+date], lot, lotId, stage, archived, [farmId+productionUnitId+archived], updatedAt',
      entityOutbox: '++seq, entityType, op, entityId, [entityType+entityId], createdAt',
      activityEvents: '&id, createdAt, source, level, dedupeKey, [dedupeKey+createdAt], farmId, productionUnitId, lotId'
    }).upgrade(async (tx) => {
      // Pre-v15 rows had a `costType` field and no accounting fields. No
      // production data existed (T3-B-01/02 landed data-plumbing only), so
      // we clear the local mirror; live rows are re-fetched from the server
      // on the next list() call.
      await tx.table('lotCostEvents').clear()
    })

    // Version 16: Harvest logs (T3-D-01, first Tier A event stream of the
    // T3-D harvest cluster; unblocks T3-B-05 cost-per-kg reporting per v2.1
    // finding #85 decision option (c)). Additive per (lot,day) — harvests
    // sum across events, unlike FishStockLog which is latest-value-wins.
    this.version(16).stores({
      tasks: '&id, updatedAt, completed, assigneeId, dueAt, parentId',
      taskOutbox: '++seq, taskId, type, createdAt',
      farms: '&id, name, createdAt',
      lots: '&id, farmId, productionUnitId, [farmId+productionUnitId], stage, [farmId+stage], [farmId+productionUnitId+stage], lotKey, updatedAt',
      lotEvents: '&id, recordedAt, lineageRootId, *lotIds, eventType, updatedAt',
      feedLogs: '&id, farmId, productionUnitId, recordedAt, lot, [farmId+productionUnitId], [farmId+productionUnitId+recordedAt]',
      mortalityLogs: '&id, farmId, productionUnitId, recordedAt, lot, [farmId+productionUnitId], [farmId+productionUnitId+recordedAt]',
      weightSampleLogs: '&id, farmId, productionUnitId, recordedAt, lot, productionRowId, [farmId+productionUnitId], [farmId+productionUnitId+recordedAt]',
      fishStockLogs: '&id, farmId, productionUnitId, recordedAt, lot, productionRowId, [farmId+productionUnitId], [farmId+productionUnitId+recordedAt]',
      harvestLogs: '&id, farmId, productionUnitId, recordedAt, lot, productionRowId, [farmId+productionUnitId], [farmId+productionUnitId+recordedAt]',
      lotCostEvents: '&id, farmId, productionUnitId, lotId, recordedAt, categoryPrefix, categoryCode, costClassification, sourceEventType, sourceEventId, [farmId+productionUnitId], [farmId+productionUnitId+recordedAt], [lotId+recordedAt]',
      inventoryItems: '&id, farmId, category, [farmId+category], updatedAt, name',
      productionTableRows: '&pk, id, date, farmId, productionUnitId, [farmId+productionUnitId], [farmId+productionUnitId+date], lot, lotId, stage, archived, [farmId+productionUnitId+archived], updatedAt',
      entityOutbox: '++seq, entityType, op, entityId, [entityType+entityId], createdAt',
      activityEvents: '&id, createdAt, source, level, dedupeKey, [dedupeKey+createdAt], farmId, productionUnitId, lotId'
    })

    // Version 17: Crew Mode Charter D-4 shared identity mechanism. Offline
    // cache of the tenant's operator roster (PIN hashes, never raw PINs) so
    // PIN entry / switch-operator works with zero connectivity.
    this.version(17).stores({
      tasks: '&id, updatedAt, completed, assigneeId, dueAt, parentId',
      taskOutbox: '++seq, taskId, type, createdAt',
      farms: '&id, name, createdAt',
      lots: '&id, farmId, productionUnitId, [farmId+productionUnitId], stage, [farmId+stage], [farmId+productionUnitId+stage], lotKey, updatedAt',
      lotEvents: '&id, recordedAt, lineageRootId, *lotIds, eventType, updatedAt',
      feedLogs: '&id, farmId, productionUnitId, recordedAt, lot, [farmId+productionUnitId], [farmId+productionUnitId+recordedAt]',
      mortalityLogs: '&id, farmId, productionUnitId, recordedAt, lot, [farmId+productionUnitId], [farmId+productionUnitId+recordedAt]',
      weightSampleLogs: '&id, farmId, productionUnitId, recordedAt, lot, productionRowId, [farmId+productionUnitId], [farmId+productionUnitId+recordedAt]',
      fishStockLogs: '&id, farmId, productionUnitId, recordedAt, lot, productionRowId, [farmId+productionUnitId], [farmId+productionUnitId+recordedAt]',
      harvestLogs: '&id, farmId, productionUnitId, recordedAt, lot, productionRowId, [farmId+productionUnitId], [farmId+productionUnitId+recordedAt]',
      lotCostEvents: '&id, farmId, productionUnitId, lotId, recordedAt, categoryPrefix, categoryCode, costClassification, sourceEventType, sourceEventId, [farmId+productionUnitId], [farmId+productionUnitId+recordedAt], [lotId+recordedAt]',
      inventoryItems: '&id, farmId, category, [farmId+category], updatedAt, name',
      productionTableRows: '&pk, id, date, farmId, productionUnitId, [farmId+productionUnitId], [farmId+productionUnitId+date], lot, lotId, stage, archived, [farmId+productionUnitId+archived], updatedAt',
      entityOutbox: '++seq, entityType, op, entityId, [entityType+entityId], createdAt',
      activityEvents: '&id, createdAt, source, level, dedupeKey, [dedupeKey+createdAt], farmId, productionUnitId, lotId',
      operatorRoster: '&tenantMembershipId, actorId'
    })
  }
}
